"use client";

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { toast } from "sonner";
import {
  StaffWorkingHours,
  TimeTrackingStatus,
  HistoryQueryParams,
  ReportsQueryParams,
} from "@/types/staffTimeTracking.types";
import { staffTimeTrackingService } from "@/services/staffTimeTrackingService";
import { HistoryResponse } from "@/types/staffTimeTracking.types";
import { TimeTrackingReportsResponse } from "@/types/staffTimeTracking.types";

interface StaffTimeTrackingContextType {
  currentSession: StaffWorkingHours | null;
  loading: boolean;
  error: string | null;
  startTracking: () => Promise<void>;
  pauseTracking: () => Promise<void>;
  resumeTracking: () => Promise<void>;
  endTracking: () => Promise<void>;
  refreshCurrentSession: () => Promise<void>;
  getHistory: (params?: HistoryQueryParams) => Promise<HistoryResponse["data"]>;
  getReports: (
    params?: ReportsQueryParams
  ) => Promise<TimeTrackingReportsResponse["data"]>;
}

const StaffTimeTrackingContext =
  createContext<StaffTimeTrackingContextType | undefined>(undefined);

export function StaffTimeTrackingProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [currentSession, setCurrentSession] =
    useState<StaffWorkingHours | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const refreshCurrentSession = useCallback(async () => {
    try {
      const session = await staffTimeTrackingService.getCurrentSession();
      setCurrentSession(session);
      setError(null);
    } catch (error: any) {
      console.error("Error refreshing current session:", error);
      // Don't set error for 404 or 401 (no session exists or unauthorized)
      if (error.response?.status !== 404 && error.response?.status !== 401) {
        setError(error.message || "Failed to fetch current session");
      } else {
        setCurrentSession(null);
      }
    }
  }, []);

  const startTracking = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const session = await staffTimeTrackingService.startTracking();
      setCurrentSession(session);
      toast.success("Time tracking started successfully");
      // Start polling for updates
      startPolling();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to start time tracking";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const pauseTracking = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const session = await staffTimeTrackingService.pauseTracking();
      setCurrentSession(session);
      toast.success("Time tracking paused successfully");
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to pause time tracking";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const resumeTracking = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const session = await staffTimeTrackingService.resumeTracking();
      setCurrentSession(session);
      toast.success("Time tracking resumed successfully");
      // Resume polling
      startPolling();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to resume time tracking";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const endTracking = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const session = await staffTimeTrackingService.endTracking();
      setCurrentSession(session);
      toast.success("Time tracking ended successfully");
      // Stop polling when session ends
      stopPolling();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to end time tracking";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const getHistory = useCallback(
    async (params?: HistoryQueryParams) => {
      try {
        return await staffTimeTrackingService.getHistory(params);
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch history";
        // Only show toast for non-401 errors (401 means unauthorized, which is expected for non-admins)
        if (error.response?.status !== 401) {
          toast.error(errorMessage);
        }
        throw error;
      }
    },
    []
  );

  const getReports = useCallback(
    async (params?: ReportsQueryParams) => {
      try {
        return await staffTimeTrackingService.getReports(params);
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch reports";
        // Only show toast for non-401 errors (401 means unauthorized, which is expected for non-admins)
        if (error.response?.status !== 401) {
          toast.error(errorMessage);
        }
        throw error;
      }
    },
    []
  );

  const startPolling = useCallback(() => {
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    // Poll every 30 seconds
    pollingIntervalRef.current = setInterval(() => {
      refreshCurrentSession();
    }, 30000);
  }, [refreshCurrentSession]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    refreshCurrentSession();
  }, [refreshCurrentSession]);

  // Start/stop polling based on session status
  useEffect(() => {
    if (
      currentSession &&
      (currentSession.status === "ACTIVE" || currentSession.status === "PAUSED")
    ) {
      startPolling();
    } else {
      stopPolling();
    }

    // Cleanup on unmount
    return () => {
      stopPolling();
    };
  }, [currentSession, startPolling, stopPolling]);

  const value: StaffTimeTrackingContextType = {
    currentSession,
    loading,
    error,
    startTracking,
    pauseTracking,
    resumeTracking,
    endTracking,
    refreshCurrentSession,
    getHistory,
    getReports,
  };

  return (
    <StaffTimeTrackingContext.Provider value={value}>
      {children}
    </StaffTimeTrackingContext.Provider>
  );
}

export function useStaffTimeTracking() {
  const context = useContext(StaffTimeTrackingContext);
  if (context === undefined) {
    throw new Error(
      "useStaffTimeTracking must be used within a StaffTimeTrackingProvider"
    );
  }
  return context;
}

